import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { transportProviderAPI } from '../../services/api';

const fmt = (n) => Number(n || 0).toLocaleString('ar-SD', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PERIOD_LABELS = { daily: 'يومي', weekly: 'أسبوعي', monthly: 'شهري' };

// Read-only earnings view — no settlement/payment action yet (matches the
// super-admin side's "عمولات مزودي النقل" tab, which is also read-only for
// now). Shows the same rides/cargo breakdown across both markets this
// provider is registered for.
const TransportProviderCommissionPage = () => {
  const [periodType, setPeriodType] = useState('monthly');
  const [rows, setRows]             = useState([]);
  const [commissionRate, setCommissionRate] = useState(null);
  const [loading, setLoading]       = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    transportProviderAPI.getMyCommissionSummary({ periodType, periods: 6 })
      .then((res) => {
        setRows(res.data.data?.rows || []);
        setCommissionRate(res.data.data?.commissionRate ?? null);
      })
      .catch(() => toast.error('فشل تحميل بيانات العمولات'))
      .finally(() => setLoading(false));
  }, [periodType]);

  useEffect(() => { load(); }, [load]);

  const hasAnyData = rows.some((r) => r.rides.orderCount > 0 || r.cargo.orderCount > 0);

  return (
    <div dir="rtl" className="p-4 sm:p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-xl font-black text-gray-800">💰 عمولاتي</h1>
        {commissionRate != null && (
          <span className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-full font-semibold">
            نسبة عمولة سافر: {commissionRate}%
          </span>
        )}
      </div>
      <p className="text-sm text-gray-500 mb-5">ملخص العمولة المستحقة لسافر وصافي مستحقاتك، لكل من طلبات المشاوير والنقل العام المكتملة.</p>

      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit mb-5">
        {Object.entries(PERIOD_LABELS).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setPeriodType(key)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
              periodType === key ? 'bg-white shadow text-gray-800' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-16 text-center text-gray-400">جاري التحميل...</div>
      ) : !hasAnyData ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center text-gray-400">
          لا توجد طلبات مكتملة في هذه الفترات بعد
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((row, i) => {
            const totalCommission = row.rides.commissionAmount + row.cargo.commissionAmount;
            const totalReceivable = row.rides.providerReceivable + row.cargo.providerReceivable;
            if (row.rides.orderCount === 0 && row.cargo.orderCount === 0) return null;
            return (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="font-bold text-gray-800">{row.period.label}</p>
                    <p className="text-xs text-gray-400">{row.period.start} — {row.period.end}</p>
                  </div>
                  <div className="text-left">
                    <p className="text-xs text-gray-400">صافي مستحقاتك</p>
                    <p className="font-bold text-emerald-700">{fmt(totalReceivable)} SDG</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="bg-blue-50 rounded-xl p-3">
                    <p className="text-xs font-bold text-blue-700 mb-1">🚕 مشاوير</p>
                    <p className="text-xs text-gray-600">{row.rides.orderCount} مشوار مكتمل</p>
                    <p className="text-xs text-gray-600">الإيراد: {fmt(row.rides.totalRevenue)}</p>
                    <p className="text-xs text-gray-600">عمولة سافر: {fmt(row.rides.commissionAmount)}</p>
                    <p className="text-sm font-bold text-blue-700 mt-1">مستحقك: {fmt(row.rides.providerReceivable)}</p>
                  </div>
                  <div className="bg-purple-50 rounded-xl p-3">
                    <p className="text-xs font-bold text-purple-700 mb-1">📦 نقل عام</p>
                    <p className="text-xs text-gray-600">{row.cargo.orderCount} طلب مكتمل</p>
                    <p className="text-xs text-gray-600">الإيراد: {fmt(row.cargo.totalRevenue)}</p>
                    <p className="text-xs text-gray-600">عمولة سافر: {fmt(row.cargo.commissionAmount)}</p>
                    <p className="text-sm font-bold text-purple-700 mt-1">مستحقك: {fmt(row.cargo.providerReceivable)}</p>
                  </div>
                </div>
                <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
                  <span>إجمالي عمولة سافر لهذه الفترة</span>
                  <span className="font-bold text-gray-700">{fmt(totalCommission)} SDG</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TransportProviderCommissionPage;
